import {
  getSelectedColorFromSelectedOptions,
  getSelectedSizeFromSelectedOptions,
} from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import {
  FREE_TOTE_SHOPIFY_ID,
  FREE_TOTE_VARIANT_ID,
  KERRY,
} from '@src/quince-site-common/src/hooks/cart/constants/freeGiftConstants'
import { CartLineItem } from '@src/quince-site-common/src/services/cartService/cart.service.types'

import Product from '../../../../domains/Product'
import productHelper from '../../../../helpers/productHelper'
import { isInNewCartExperiment } from '../../../../hooks/cart/hooks/new-cart-exp-helper'
import {
  getCustomAttributes,
  getLineItemDetails,
  getLineItemId,
  getSelectedOptionsFromLineItem,
} from '../../../../hooks/cart/hooks/new-cart-helper'
import giftService from '../../../../services/giftService'
import productService from '../../../../services/productService'
import { decodeShopifyIdFromGraphqlIdResponse } from '../../../../utils/decodeShopifyIdFromGraphqlIdResponse'

import { IShopifyCheckoutLineItem } from './../../../../domains/shopify/ShopifyCheckout'
import { ShopifyEdgeNode } from './../../../../domains/shopify/ShopifyEdgeNodeWrapper'

export interface LineItemDetails {
  mergeOption: string
  productLink: string
  internalVariantId: number | null
  internalProductId: number
  color?: string
  price?: number
}

export const getCartConfig = async (lineItems: ShopifyEdgeNode<IShopifyCheckoutLineItem>[]) => {
  const lineItemDetails = isInNewCartExperiment()
    ? getLineItemDetails(lineItems as unknown as CartLineItem[])
    : await getLineItemConfig(lineItems)

  const shopifyVariantIds = Object.keys(lineItemDetails)
    .map(responseId => Number(decodeShopifyIdFromGraphqlIdResponse(responseId)))
    .filter(id => id !== FREE_TOTE_VARIANT_ID)

  const giftEligibleVariantIdsForTote = await giftService.getGiftEligibleVariantIds(shopifyVariantIds, KERRY)

  return {
    lineItemDetails,
    giftShopifyIdToEligibleVariantIds: {
      [FREE_TOTE_SHOPIFY_ID]: giftEligibleVariantIdsForTote,
    },
  }
}

export const getLineItemConfig = async (
  lineItems: ShopifyEdgeNode<IShopifyCheckoutLineItem>[],
): Promise<Record<string, LineItemDetails>> => {
  const shopifyProductIdsSet = new Set<number>()

  lineItems.forEach(lineItem => {
    const shopifyProductId = getLineItemId(lineItem)
    if (shopifyProductId) {
      shopifyProductIdsSet.add(Number(shopifyProductId))
    }
  })

  const shopifyProductIds = Array.from(shopifyProductIdsSet)
  if (shopifyProductIds.length > 0) {
    const products = await productService.getProducts({
      shopifyProductIds,
      useLite: true,
      isChildProduct: true,
    })
    const productByShopifyProductId: Record<string, Product> = {}

    // This is fix for 100+ variants
    products.forEach(product => {
      const shopifyProductIds = Array.from(new Set(product.variants?.map(variant => variant.shopifyProductId)))
      shopifyProductIds.forEach(shopifyProductId => {
        productByShopifyProductId[shopifyProductId] = product
      })
    })

    const lineItemDetailsByVariantId: Record<string, LineItemDetails> = {}
    lineItems.forEach(lineItem => {
      let color, size
      const _shopifyProductId = decodeShopifyIdFromGraphqlIdResponse(lineItem.node?.variant.product.id)
      const variantId = lineItem.node?.variant.id
      if (!_shopifyProductId || !variantId) return

      const shopifyProductId = Number(_shopifyProductId)
      const product = productByShopifyProductId[shopifyProductId]

      if (product) {
        const selectedOptions = getSelectedOptionsFromLineItem(lineItem) as ProductVariantOption[] | undefined
        if (selectedOptions) {
          color = getSelectedColorFromSelectedOptions(selectedOptions)?.split(' ').join('-')
          size = getSelectedSizeFromSelectedOptions(selectedOptions)
        }

        const selectedVariant = selectedOptions ? productHelper.getVariantsForOptions(product, selectedOptions) : null

        const urlencoded = new URLSearchParams()
        if (color) urlencoded.append('color', color)
        if (size) urlencoded.append('size', size)
        lineItemDetailsByVariantId[variantId] = {
          mergeOption: product.mergeOption || '',
          productLink: product.slug ? `/${product.slug}?${urlencoded.toString()}` : '',
          internalProductId: product.id,
          internalVariantId: selectedVariant?.length === 1 && selectedVariant[0] ? selectedVariant[0]?.id : null,
          color: getSelectedColorFromSelectedOptions(selectedOptions),
          price: productHelper.getQuincePriceForOptions(product, selectedOptions),
        }
      }
    })
    return lineItemDetailsByVariantId
  }

  return {}
}

export const isLineItemNodeFreeProduct = (lineItemNode: IShopifyCheckoutLineItem) => {
  return (
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    (getCustomAttributes(lineItemNode) || []).findIndex(customAttribute => customAttribute.key === '_FREE_SWATCH_') !==
    -1
  )
}

export const attrWithoutUnderscore = (str: string) => {
  if (str.startsWith('_')) {
    return str.substring(1)
  }
  return str
}

export const stripHtml = (str: string, withSpaces = false) => {
  return str.replace(/(<([^>]+)>)/gi, withSpaces ? ' ' : '')
}
